import React, { Suspense } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { Switch, Route, Redirect } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { Backdrop } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { motion } from "framer-motion";
import { ClimbingBoxLoader } from "react-spinners";

import * as serviceWorker from "./serviceWorker";
import Spinner from "./components/Spinner";
import { setWebsocketConnection } from "./reducers/WebsocketReducer";
import { setUserAuthenticated, setUserData } from "./reducers/UserReducer";
import { notify, notifyPanel } from "./reducers/NotifierReducer";
import { getAccessPermission } from "./reducers/PermissionsReducer";

// Layout Blueprints
import { MinimalLayout, MainLayout } from "./layout-blueprints";

// Pages
import PageLogin from "./pages/PageLogin";
import Page404 from "./pages/Page404";
import PageResetPassword from "./pages/PageResetPassword";
import routeDefinitions, { defaultUrl } from "./routeDefinitions";
import client from "./feathersClient";
import { routeToModuleMap } from "./utils/enums";

const checkNecessaryPermissions = (pathname, userAgency) => {
  const moduleDef = routeToModuleMap[pathname];

  if (!moduleDef) {
    return { allowed: true };
  }

  const modulePermissions = getAccessPermission(
    moduleDef.area,
    moduleDef.key,
    userAgency
  );

  if (!modulePermissions) {
    return { allowed: null };
  }

  if (!modulePermissions.Read) {
    return { allowed: false };
  }

  return { allowed: true };
};

const ProtectedRoute = ({
  component: Component,
  userAgency,
  isAuthenticated,
  ...rest
}) => {
  const dispatch = useDispatch();

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!isAuthenticated) {
          return (
            <Redirect
              to={{ pathname: "/Login", state: { from: props.location } }}
            />
          );
        }

        const { allowed } = checkNecessaryPermissions(
          props.location?.pathname,
          userAgency
        );

        if (allowed === null) {
          return (
            <Backdrop open>
              <CircularProgress color="primary" />
            </Backdrop>
          );
        }

        if (allowed === false) {
          dispatch(
            notify("You do not have permission to access this page", "warning")
          );
          return (
            <Redirect
              to={{ pathname: "/Main", state: { from: props.location } }}
            />
          );
        }

        return <Component {...props} />;
      }}
    />
  );
};

const LoginRoute = ({ component: Component, isAuthenticated, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      isAuthenticated === true ? (
        <Redirect
          to={{
            pathname: defaultUrl,
            state: { from: props.location },
          }}
        />
      ) : (
        <Component {...props} />
      )
    }
  />
);

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
}));

const Routes = (props) => {
  const {
    isAuthenticated,
    setWebsocketConnection,
    setUserAuthenticated,
    setUserData,
    setLoggedInUserData,
    configLoaded,
    circularLoading,
    globalCircularLoading,
  } = props;

  const userAgency = useSelector((state) => state.user.userAgency);
  const classes = useStyles();

  React.useEffect(() => {
    // handle service worker updates
    const updateApp = (registration) => {
      window.localStorage.clear();
      const waitingServiceWorker = registration.waiting;

      if (waitingServiceWorker) {
        waitingServiceWorker.addEventListener("statechange", (event) => {
          if (event.target.state === "activated") {
            window.location.reload();
          }
        });
        waitingServiceWorker.postMessage({ type: "SKIP_WAITING" });
      }
    };
    const openNotification = (registration) => {
      const notification = {
        title: `Application Update Available!`,
        message: `New release of Image Capture Application has been released. It is highly recommended to launch the new version!!`,
        position: "tc",
        level: "warning",
        autoDismiss: 0,
        action: {
          label: "LAUNCH!",
          callback: () => updateApp(registration),
        },
      };
      props.notifyPanel(notification, "success");
    };
    serviceWorker.register({
      onSuccess: () => console.log("service worker registered!"),
      onUpdate: (reg) => {
        if (isAuthenticated) {
          openNotification(reg);
          props.registerAppUpdate(reg);
        } else {
          updateApp(reg);
        }
      },
    });
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    async function establishConnection() {
      try {
        const user = await client.reAuthenticate({ type: "ams" });
        setWebsocketConnection(client);
        setUserData(user);
        setUserAuthenticated(true);
      } catch (error) {
        if (error.code === 401) {
          setUserAuthenticated(false);
          setUserData(null);
          setWebsocketConnection(client);
        }
        setWebsocketConnection(client);
      }
    }
    establishConnection();
    // eslint-disable-next-line
  }, [setWebsocketConnection, setUserAuthenticated, setLoggedInUserData]);

  const pageVariants = {
    initial: { opacity: 0, scale: 0.99, width: "100%", height: "100%" },
    in: { opacity: 1, scale: 1 },
    out: { opacity: 0, scale: 1.01 },
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.4,
  };

  const SuspenseLoading = () => (
    <div className="d-flex align-items-center flex-column vh-100 justify-content-center text-center py-3">
      <div className="d-flex align-items-center flex-column px-4">
        <ClimbingBoxLoader color={"#0153a3"} loading={true} />
      </div>
      <div className="text-muted font-size-xl text-center pt-3">
        Please wait while we load the view...
      </div>
    </div>
  );

  const loadingPage = () => <Spinner />;

  return (
    <Suspense fallback={<SuspenseLoading />}>
      <Switch>
        <Redirect exact from="/" to="/Login" />
        <Route path={["/Login"]}>
          <MinimalLayout>
            <motion.div
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
            >
              <LoginRoute
                path="/Login"
                component={PageLogin}
                isAuthenticated={isAuthenticated}
              />
            </motion.div>
          </MinimalLayout>
        </Route>

        <Route path={["/Reset-password/:hash"]}>
          <MinimalLayout>
            <PageResetPassword isAuthenticated={isAuthenticated} />
          </MinimalLayout>
        </Route>

        {routeDefinitions.map((route, key) => {
          const Layout = route.layout;
          return (
            <Route exact path={[route.url]} key={key}>
              <>
                <Backdrop className={classes.backdrop} open={globalCircularLoading || circularLoading}>
                  <CircularProgress color="inherit" />
                </Backdrop>
                <Layout>
                  <motion.div
                    initial="initial"
                    animate="in"
                    exit="out"
                    variants={pageVariants}
                    transition={pageTransition}
                  >
                    <ProtectedRoute
                      path={route.url}
                      component={configLoaded ? route.component : loadingPage}
                      isAuthenticated={isAuthenticated}
                      userAgency={userAgency}
                    />
                  </motion.div>
                </Layout>
              </>
            </Route>
          );
        })}

        <Route>
          <MainLayout>
            <motion.div
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}
            >
              <ProtectedRoute
                component={Page404}
                isAuthenticated={isAuthenticated}
              />
            </motion.div>
          </MainLayout>
        </Route>
      </Switch>
    </Suspense>
  );
};

const mapStateToProps = (state) => ({
  network: state.offline,
  isAuthenticated: state.user.isAuthenticated,
  user: state.user,
  configLoaded: state.config.loaded,
  circularLoading: state.ui.circularLoading,
  globalCircularLoading: state.ui.globalCircularLoading,
});

export default connect(mapStateToProps, {
  setWebsocketConnection,
  setUserAuthenticated,
  setUserData,
  notifyPanel,
})(Routes);
